import { castArray } from 'lodash-es';
import { filter, find, map, pick, pipe, piped } from 'remeda';
import invariant from 'tiny-invariant';

import {
    Element,
    ELEMENT_TYPES,
    type InterviewSegmentElement,
    type ParagraphElement,
} from '@@editor/helpers/Element';
import {
    isUnityInterviewSegment,
    type UnityInterviewSegment,
} from '@@editor/typings/UnityElements';
import makeDefaultTextIfNull from '@@editor/utils/makeDefaultUnityTextIfNull';
import toParagraph from '@@editor/utils/toParagraph';

import { type ElementRule, type Next } from '../../types';
import { CURRENT_VERSION } from './../../constants';
import { createElementFromSingleLineState } from './../../deserializeNodes';

const deserializeQuestion =
    (next: Next<ParagraphElement>) => (question: UnityInterviewSegment['question']) =>
        pipe(
            question,
            makeDefaultTextIfNull,
            next,
            castArray,
            createElementFromSingleLineState(ELEMENT_TYPES.INTERVIEW_SEGMENT_QUESTION),
        );

const deserializeAnswers =
    (next: Next<ParagraphElement>) => (answers: UnityInterviewSegment['answers']) =>
        pipe(
            answers,
            map(
                piped(
                    makeDefaultTextIfNull,
                    next,
                    castArray,
                    createElementFromSingleLineState(ELEMENT_TYPES.INTERVIEW_SEGMENT_ANSWER),
                ),
            ),
        );

const serializeQuestion =
    (next: Next<UnityInterviewSegment['question']>) =>
    (children: InterviewSegmentElement['children']) =>
        pipe(children, find(Element.isInterviewSegmentQuestionElement), toParagraph, next);

const serializeAnswers =
    (next: Next<UnityInterviewSegment['answers'][number]>) =>
    (children: InterviewSegmentElement['children']) =>
        pipe(
            children,
            filter(Element.isInterviewSegmentAnswerElement),
            map(piped(toParagraph, next)),
        );

type Rule = ElementRule<InterviewSegmentElement, UnityInterviewSegment>;

const interviewRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnityInterviewSegment(element)) {
            invariant(next, 'Cannot deserialize InterviewSegment without a `next` function');

            const { answers, question, type } = element;

            return {
                type,
                data: pick(element, ['templateElement']),
                children: [
                    deserializeQuestion(next)(question),
                    ...deserializeAnswers(next)(answers),
                ],
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isInterviewSegmentElement(node)) {
            invariant(next, 'Cannot serialize InterviewSegment without a `next` function');

            const { children, data, type } = node;

            return {
                ...pick(data, ['templateElement']),
                version: CURRENT_VERSION,
                type,
                question: serializeQuestion(next)(children),
                answers: serializeAnswers(next)(children),
            };
        }
    },
};

export default interviewRules;
